<template>
	<!-- 高保真模式-标记保护区 抽屉内容：仅渲染源 PDF（原文，无译文），并支持标记不翻译保护区 -->
	<div class="protectArea">
		<!-- 顶部操作栏：复用区域翻译顶部组件（整页不翻译 / 矩形 / 分割线） -->
		<regionalTranslationTopView v-if="!downloadState" :pages="pages" :ratio="1" :isEdit="true"
			:isProtectArea="true" :pageFlagStatus="pageFlagStatus" :editPdfJsonStr="editPdfJsonStr" @operatorActive="operatorActive"
			@updatePages="updatePages" @onClose="onExit" />

		<div class="protectArea_body">
			<!-- 左侧：源 PDF 渲染区 + canvas 标记叠层 -->
			<div class="protectArea_pdf">
				<div class="loading_view" v-show="downloadState">
					<loading class="loading" />
				</div>
				<div ref="pdfScrollRef" class="protectArea_scroll" v-show="!downloadState"
					:style="`height:${scrollHeight}px;`" @scroll="onScroll">
					<!-- 每一页的容器由 JS 动态 appendChild，canvas 标记层叠加在其上 -->
					<div ref="pdfPageRef" class="protectArea_pages"></div>
				</div>
			</div>

			<!-- 右侧：标记列表（复用区域翻译右侧组件，isProtectArea=true 隐藏其重新翻译/限额按钮） -->
			<div class="protectArea_right" :style="{ width: `${layoutState.rightOuterWidth}px`, flex: `0 0 ${layoutState.rightOuterWidth}px` }">
				<regionalTranslationRightView ref="rightViewRef" :isProtectArea="true" :editPdfCount="0"
					:style="{ width: `${layoutState.rightInnerWidth}px` }"
					:editPdfLimitCfgJson="{}" @updataJsonList="onJsonListChange" />
				<!-- 抽屉右侧底部操作按钮（纵向排列） -->
				<div class="protectArea_actions">
					<!-- 计费规则：固定底部,点击弹出计费规则浮层(参考 docs/img2)；进入面板时自动弹一次 -->
					<div ref="hfCostRuleRef" class="protectArea_cost_rule" @click="onShowHfCostRule">{{ $t('cost_rule_link') }}</div>
					<div v-if="hasMarkData" class="protectArea_btn protectArea_btn_primary" @click="onConfirm">
						{{ $t('high_fidelity_protect_confirm') }}
					</div>
					<div class="protectArea_btn protectArea_btn_plain" @click="onExit">
						<i class="iconfont icon-icon_exit1" style="margin-right: 4px; vertical-align: middle;" />
						{{ $t('high_fidelity_protect_exit') }}
					</div>
				</div>
			</div>
		</div>

		<!-- 退出确认层：teleport 到 body，避免被抽屉 transform 困住，实现相对浏览器视口居中；以高 z-index 压住抽屉遮罩 -->
		<Teleport to="body">
		<div v-if="showExitConfirm" class="protectArea_confirm_mask">
			<div class="protectArea_confirm_box">
				<!-- 标题：退出保护区设置确认 -->
				<div class="protectArea_confirm_title">{{ $t('high_fidelity_protect_close_title') }}</div>
				<!-- 内容：提示退出后已标记保护区会被清空且不可恢复；换行由文案内 \n + white-space 保持 -->
				<div class="protectArea_confirm_text">{{ $t('high_fidelity_protect_close_confirm') }}</div>
				<div class="protectArea_confirm_btns">
					<div class="protectArea_confirm_btn protectArea_confirm_cancel" @click="onExitConfirmCancel">
						{{ $t('cancel') }}
					</div>
					<div class="protectArea_confirm_btn protectArea_confirm_ok" @click="onExitConfirmOk">
						{{ $t('high_fidelity_protect_close_ok') }}
					</div>
				</div>
			</div>
		</div>
		</Teleport>
	</div>
</template>

<script setup lang="ts">
// 高保真模式下，翻译发起前在源 PDF 上标记“不翻译保护区”。
// 该组件只渲染源文件（原文），不涉及译文 / newPDF / 后端保存接口。
import { ref, reactive, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import useUserStore from '@/store/modules/user'
import { storeToRefs } from 'pinia'
import { useCostRule } from './useEnterpriseProtectAreaCostRule'
import { useDrawing, DrawingMode } from '@/composables/useEditPdfDrawing'
import { registerDrawing, getAllDrawings, getDrawing, clearDrawings, subscribe } from '@/composables/drawingManager'
import { lazyRender } from '@/pages/home/preview/lazyRender'
import { isMobileOrTablet } from '@/utils'
import regionalTranslationTopView from '@/components/PDFEditComponents/regionalTranslationTopView.vue'
import regionalTranslationRightView from '@/components/PDFEditComponents/regionalTranslationRightView.vue'
import { resolveProtectAreaLayout } from './enterpriseProtectAreaLayout'
import {
	buildPageFlagStatusFromEditJson,
	buildPageFlagStatusFromJsonList,
	getPageScrollTop,
	resolvePageLayoutMetrics,
	resolveCurrentPageFromScroll
} from './enterpriseProtectAreaScroll.js'

const { t } = useI18n()
const { userRights } = storeToRefs(useUserStore())
const costRule = useCostRule()
// 「计费规则」链接元素引用：自动打开浮层时作为定位锚点
const hfCostRuleRef = ref<HTMLElement | null>(null)

// 高保真计费规则文案：每页消耗字符数取 homepage 接口的 highFidelityCostWord（null 兜底 0），
// 按行拼接（{count} 单页额度，{total} = 3 页示例 = 3 × count）。点击与自动打开共用此拼接。
function buildHfCostLines() {
	const count = Number((userRights.value as any)?.highFidelityCostWord) || 0
	const total = count * 3
	return [
		t('cost_rule_hf_l1', { count: count.toLocaleString() }),
		t('cost_rule_hf_l2'),
		t('cost_rule_hf_l3', { count: count.toLocaleString(), total: total.toLocaleString() }),
		'',
		t('cost_rule_hf_l4'),
	]
}

// 点击「计费规则」链接：浮层定位到链接正上方
function onShowHfCostRule(e: MouseEvent) {
	costRule.open({ lines: buildHfCostLines(), anchorEl: e.currentTarget as HTMLElement })
}

interface Props {
	file: File
	modelValue?: Record<string, any[]>
}
const props = withDefaults(defineProps<Props>(), {
	modelValue: () => ({}),
})

const emit = defineEmits<{
	(e: 'confirm', value: Record<string, any[]>): void
	(e: 'exit'): void
}>()

// ===== 渲染相关状态 =====
const downloadState = ref(true) // 源 PDF 渲染中遮罩
const pdfPageRef = ref<HTMLElement>()
const pdfScrollRef = ref<HTMLElement>()
const containerWidth = ref(700) // 单列 PDF 渲染宽度
const scrollHeight = ref(0)
// 右侧列表与左侧 PDF 的视觉宽度会一起收缩；这三个值由同一个布局计算结果驱动。
const layoutState = reactive({
	drawerWidth: 0,
	rightOuterWidth: 340,
	rightInnerWidth: 320,
})
const renderedPdfWidth = ref(0)
let resizeRerenderTimer: ReturnType<typeof setTimeout> | null = null
let unsubscribeDrawingManager: (() => void) | null = null
const pageHeights = ref<Record<number, number>>({})
const pageTops = ref<number[]>([])
// 顶部组件需要的页码对象（与 regionalTranslationTopView 的 pagesType 对应）
const pages = reactive({ currentPage: 1, pageNumber: 0, allH: 0 })
const CMAP_URL = '/cmaps/'
const LAST_PAGE_SPACER_ID = 'protect-area-last-page-spacer'

// 回显数据：若 store 已存在该文件的保护区标记，转成顶部/列表组件所需的 json 字符串
const editPdfJsonStr = ref<string>('')
// 回显原始对象（按页索引），用于初始化每页 canvas 时注入历史数据
const editPdfJson = ref<Record<string, any> | null>(null)

// ===== 尺寸计算 =====
function applyLayoutMetrics() {
	// 计算结果与抽屉宽度保持一致：总宽度不低于 920px，右侧列表在窄屏时同步收缩。
	const nextLayout = resolveProtectAreaLayout({
		viewportWidth: document.documentElement.clientWidth,
		isMobile: isMobileOrTablet(),
	})
	layoutState.drawerWidth = nextLayout.drawerWidth
	layoutState.rightOuterWidth = nextLayout.rightOuterWidth
	layoutState.rightInnerWidth = nextLayout.rightInnerWidth
	containerWidth.value = nextLayout.pdfRenderWidth
	scrollHeight.value = document.documentElement.clientHeight - 56
	return nextLayout
}

function calcSize() {
	// 不能依赖 .protectArea clientWidth：抽屉开场动画期间布局宽度尚未稳定，
	// 退回整屏宽会导致 PDF 渲染过宽（参考 regionalTranslationPdf 同样用视口数学）。
	const nextLayout = applyLayoutMetrics()
	nextTick(syncPageLayoutMetrics)

	// 窗口缩放时同步重渲染：保留已有标记数据，让左右栏一起按新宽度重排。
	if (pages.pageNumber > 0 && !downloadState.value && renderedPdfWidth.value !== nextLayout.pdfRenderWidth) {
		schedulePdfRerender()
	}
}

function schedulePdfRerender() {
	if (resizeRerenderTimer) {
		clearTimeout(resizeRerenderTimer)
	}
	resizeRerenderTimer = setTimeout(async () => {
		resizeRerenderTimer = null
		if (!mounted || downloadState.value || pages.pageNumber <= 0) return
		try {
			const currentEditJson = await buildEditPageInfoJson()
			editPdfJson.value = currentEditJson
			try {
				editPdfJsonStr.value = Object.keys(currentEditJson).length > 0 ? JSON.stringify(currentEditJson) : ''
			} catch (e) {
				editPdfJsonStr.value = ''
			}
			pageFlagStatus.value = buildPageFlagStatusFromEditJson(currentEditJson)
			clearDrawings()
			await initPdf()
			recomputeHasMark()
		} catch (e) {
			console.error('[ProtectArea] 重新计算布局失败', e)
		}
	}, 150)
}

// 最后一页如果矮于滚动容器，必须补足底部留白，才能让最后一页顶部滚到视口顶部并触发页级选中。
function syncPageLayoutMetrics() {
	const measuredTops = getMeasuredPageTops()
	const { pageTops: resolvedPageTops, lastPageBottomMargin } = resolvePageLayoutMetrics({
		measuredTops,
		measuredHeights: getMeasuredPageHeights(),
		pageHeights: pageHeights.value,
		pageCount: pages.pageNumber,
		containerHeight: pdfScrollRef.value?.clientHeight || scrollHeight.value,
	})
	pageTops.value = resolvedPageTops
	syncLastPageSpacer(lastPageBottomMargin)
}

function syncLastPageSpacer(height: number) {
	if (!pdfPageRef.value || pages.pageNumber <= 0) return
	const spacer = ensureLastPageSpacer()
	const spacerHeight = Math.max(0, Number(height) || 0)
	// 最后一页后追加真实空白节点，比 padding/margin 更适合懒渲染/无限滚动容器，
	// 同时通过 getPageElements() 排除该节点，避免它参与页码判断。
	spacer.style.height = `${spacerHeight}px`
	spacer.style.minHeight = `${spacerHeight}px`
	spacer.style.width = '100%'
	spacer.style.flexShrink = '0'
	spacer.style.pointerEvents = 'none'
}

function ensureLastPageSpacer() {
	let spacer = document.getElementById(LAST_PAGE_SPACER_ID) as HTMLElement | null
	if (!spacer) {
		spacer = document.createElement('div')
		spacer.id = LAST_PAGE_SPACER_ID
		spacer.setAttribute('aria-hidden', 'true')
	}
	if (pdfPageRef.value && spacer.parentElement !== pdfPageRef.value) {
		pdfPageRef.value.appendChild(spacer)
	}
	return spacer
}

function getPageElements() {
	if (!pdfPageRef.value) return []
	return Array.from(pdfPageRef.value.children)
		.filter((child): child is HTMLElement => child instanceof HTMLElement && !!child.dataset.index)
}

function getMeasuredPageTops() {
	if (!pdfScrollRef.value || !pdfPageRef.value) return []
	const scrollRect = pdfScrollRef.value.getBoundingClientRect()
	return getPageElements().map((child) => {
		const rect = child.getBoundingClientRect()
		return Math.max(0, rect.top - scrollRect.top + pdfScrollRef.value!.scrollTop)
	})
}

function getMeasuredPageHeights() {
	if (!pdfPageRef.value) return []
	return getPageElements().map((child) => {
		const rect = child.getBoundingClientRect()
		return Math.max(0, rect.height || child.offsetHeight || 0)
	})
}

// ===== 历史标记数据回显 =====
// 把 store 中保存的 editPageInfoJson（{ "1":[{edit_rect_type,ignore_type,data:[[x,y],[x,y]]}] }）
// 还原为每页 canvas 可消费的格式（与 regionalTranslationPdf.formatPdfEditData 一致）
function formatPdfEditData(index: number, editData: Record<string, any> | null): any[] | null {
	const pageData = editData?.[index]
	if (!pageData || !Array.isArray(pageData)) return null
	return pageData.map((item: any) => {
		if (item.edit_rect_type === 'page') {
			return { ...item, text: t('doclingo_this_page_is_not_being_translated'), isPage: true }
		}
		// 后台格式 data 为扁平数组，按每 4 个点还原成 [[x1,y1],[x2,y2]]
		const chunkedData: number[][] = []
		for (let i = 0; i < item.data.length; i += 4) {
			chunkedData.push([item.data[i], item.data[i + 1]], [item.data[i + 2], item.data[i + 3]])
		}
		return { ...item, isPage: false, data: chunkedData }
	})
}

// ===== 每页 canvas 标记初始化 =====
const drawingInstances: Record<string, ReturnType<typeof useDrawing>> = {}
function initEditCanvasDrawing(pageId: string, height: number, width: number, index: number, scale: number, formattedData: any) {
	return new Promise<void>((resolve) => {
		nextTick(() => {
			const drawing = useDrawing(pageId)
			drawing.init(height, width, t('doclingo_select_area'), index, scale, () => {
				// 注册到全局 drawingManager，右侧列表组件通过它订阅画布变化
				registerDrawing(pageId, drawing)
				drawingInstances[pageId] = drawing
				// 直接给本页 drawing 实例挂变更回调（与 drawingManager 同机制，但绑在我们自己创建的实例上，
				// 不经全局 subscribe 时序，确保画框/分割/整页后 hasMarkData 立即重算 → 按钮与关闭确认即时生效）
				drawing.onDrawingChange(() => {
					recomputeHasMark()
				})
				resolve()
				if (drawing && !formattedData) {
					drawing.lines.value = []
					drawing.rects.value = []
					drawing.pages.value = []
				}
			})
		})
	})
}

// 所有 canvas 初始化后，把回显数据写回对应页画布
let setDataTimer: ReturnType<typeof setTimeout> | null = null
function setAllCanvasData(delay = 0) {
	if (setDataTimer) {
		clearTimeout(setDataTimer)
		setDataTimer = null
	}
	setDataTimer = setTimeout(() => {
		const drawings = getAllDrawings()
		Object.values(drawings).forEach((item: any, idx: number) => {
			if (editPdfJson.value) {
				const pageIndexStr = String(idx + 1)
				if (Object.hasOwn(editPdfJson.value, pageIndexStr)) {
					const formattedData = formatPdfEditData(idx + 1, editPdfJson.value)
					item.importDataSetCanvasUiEndData(formattedData, idx + 1)
				}
			}
		})
	}, delay)
}

async function loadPdfBytes(): Promise<Uint8Array> {
	const buf = await props.file.arrayBuffer()
	return new Uint8Array(buf)
}

// ===== 源 PDF 渲染（仅原文，支持本地 File 或远端 URL 两种来源） =====
async function initPdf() {
	downloadState.value = true
	try {
		clearDrawings()
		if (setDataTimer) {
			clearTimeout(setDataTimer)
			setDataTimer = null
		}
		pages.allH = 0
		pageHeights.value = {}
		pageTops.value = []
		if (pdfPageRef.value) {
			pdfPageRef.value.innerHTML = ''
		}
		const PDF = await useNuxtApp().$PdfUtils.getPDF()
		const data = await loadPdfBytes()
		const pdfDocumentInfo = await PDF.getDocument({
			data,
			cMapUrl: CMAP_URL,
			cMapPacked: true
		}).promise
		// 复用 PdfUtils.getPdfPageInfo 所需的 { _pdf, _totalPage } 结构
		const pageMap = { _pdf: pdfDocumentInfo, _totalPage: pdfDocumentInfo._pdfInfo.numPages }
		const pageInfo = await useNuxtApp().$PdfUtils.getPdfPageInfo(pageMap)

		pages.pageNumber = pageMap._totalPage
		pages.currentPage = 1

		const width = containerWidth.value
		const elements: HTMLElement[] = []
		const pagePromises: Promise<void>[] = []

		for (let i = 1; i <= pageMap._totalPage; i++) {
			// 容器 id 与 PDF canvas 前缀必须分属不同命名空间：
			// lazyRender 会 getElementById(canvasName+index) 命中即当 canvas 用，
			// 若容器 id 与之同名会把 <div> 误当 <canvas> 导致渲染崩溃（参考 regionalTranslationPdf 的 left-page-/left-canvas-1- 双命名）。
			// 同时用独立 protect- 前缀，避免与 PDFTranslate 的全局 drawingManager 串扰。
			const pageId = `protect-page-${i}`
			const pageDiv = document.createElement('div')
			pageDiv.setAttribute('id', pageId)
			pageDiv.setAttribute('style', 'position: relative; display: flex;')
			pageDiv.setAttribute('data-index', `${i}`)
			pageDiv.style.minHeight = '100vh'
			const spacer = document.getElementById(LAST_PAGE_SPACER_ID)
			pdfPageRef.value?.insertBefore(pageDiv, spacer?.parentElement === pdfPageRef.value ? spacer : null)

			useNuxtApp().$PdfUtils.renderPageToInfo((pageInfo as any)[i], pageDiv, {
				scale: 1,
				width
			}, (scale: number, height: number) => {
				pageDiv.style.width = width + 'px'
				pageDiv.style.height = height + 'px'
				pageDiv.style.minHeight = height + 'px'
				pages.allH += height
				pageHeights.value[i] = height
				// 回显：当前页若有历史标记，则准备格式化数据
				let formattedData: any[] | null = null
				if (editPdfJson.value && Object.hasOwn(editPdfJson.value, String(i))) {
					formattedData = formatPdfEditData(i, editPdfJson.value)
				}
				pagePromises.push(initEditCanvasDrawing(pageId, height, width, i, scale, formattedData))
			}, `protect-canvas-1-${i}`)
			elements.push(pageDiv)
		}

		const array = Object.values(pageInfo as any)
		lazyRender(elements, array as any, {
			scale: 1,
			width,
			userLevel: 0,
			notRenderTextLayer: true
		} as any, 'protect-canvas-1-')

		await Promise.all(pagePromises)
		syncPageLayoutMetrics()
		setAllCanvasData(0)
		setTimeout(async () => {
			downloadState.value = false
			await nextTick()
			renderedPdfWidth.value = width
			syncPageLayoutMetrics()
		}, 500)
	} catch (e) {
		console.error('[ProtectArea] 源 PDF 渲染失败', e)
		downloadState.value = false
	}
}

// ===== 顶部操作栏交互（与 PDFTranslate 接法一致）=====
const operatorActiveType = ref('page')
function operatorActive(payload: { action: string, isCurrentlyFlag?: boolean }) {
	operatorActiveType.value = payload.action
	openRegional(payload.action, payload.isCurrentlyFlag)
}
// 顶部页码输入：滚动定位到指定页
function updatePages(payload: { currentPage: number }) {
	const target = Number(payload.currentPage)
	if (!pdfScrollRef.value || !pages.pageNumber) return
	syncPageLayoutMetrics()
	pdfScrollRef.value.scrollTop = getPageScrollTop(pageTops.value, target)
	// 立即同步当前页（滚动事件也会兜底，这里保证输入即时反馈与整页定位准确）
	const clamped = Math.min(Math.max(1, target), pages.pageNumber)
	pages.currentPage = clamped
	localCurrentPage.value = clamped
}

// 当前可视页码（用于整页不翻译定位），随滚动更新
const localCurrentPage = ref(1)
function onScroll() {
	if (!pdfScrollRef.value || !pages.pageNumber) return
	syncPageLayoutMetrics()
	const newPage = resolveCurrentPageFromScroll({
		scrollTop: pdfScrollRef.value.scrollTop,
		clientHeight: pdfScrollRef.value.clientHeight,
		scrollHeight: pdfScrollRef.value.scrollHeight,
		pageTops: pageTops.value
	})
	if (newPage !== pages.currentPage) {
		// 同步给顶部组件：regionalTranslationTopView 对 props.pages 做了 deep watch，
		// 改 pages.currentPage 才能让其页码输入框跟随滚动更新
		pages.currentPage = newPage
		localCurrentPage.value = newPage // 整页不翻译按当前页定位用
	}
}

// 切换绘制模式：整页不翻译 / 矩形 / 分割线（逻辑对齐 regionalTranslationPdf.openRegional）
const pageFlagStatus = ref<Record<number, boolean>>({})
function openRegional(type?: string, isCurrentlyFlag?: boolean) {
	const drawings = getAllDrawings()
	if (type === 'rectangle' || type === 'line') {
		const mode = type === 'rectangle' ? DrawingMode.Rectangle : DrawingMode.Line
		Object.entries(drawings).forEach(([id, d]: any) => {
			// 整页不翻译的页：禁止再进行区域选择/分割（跳过，不重新 enable / 切模式）
			const pno = parseInt(id.split('-')[2], 10)
			if (pageFlagStatus.value[pno]) return
			d.setCanvasZIndex('20')
			d.enable()
			d.setDrawingMode(mode)
		})
	} else if (type === 'pageFlag') {
		const pageId = `protect-page-${localCurrentPage.value}`
		const drawingsItem = getDrawing(pageId)
		if (!drawingsItem) return
		const currentPage = localCurrentPage.value
		// 以父组件维护的页级真实状态为准，避免顶部按钮状态滞后导致“先取消再选择”的二次点击问题。
		const isFlagged = Boolean(pageFlagStatus.value[currentPage])
		if (isFlagged) {
			drawingsItem.instance.setDrawingMode(DrawingMode.PageFlag, { pageIndex: currentPage })
			pageFlagStatus.value[currentPage] = false
		} else {
			drawingsItem.instance.setDrawingMode(DrawingMode.PageFlag, {
				pageIndex: currentPage,
				text: t('doclingo_this_page_is_not_being_translated'),
				textColor: '#F09308',
				bgColor: 'rgba(252, 233, 206, 1)'
			})
			pageFlagStatus.value[currentPage] = true
		}
	}
}

// ===== 标记列表是否有数据（决定底部按钮显隐）=====
const hasMarkData = ref(false)
let mounted = true
// regionalTranslationRightView 的 ref：直接读它维护的权威 jsonList
// （截图中页面上渲染出来的 P1/P4 区域列表就是它），最可靠、与界面所见完全一致
const rightViewRef = ref<any>(null)
// 统一重算入口：以 rightView 的 jsonList 长度为准
function recomputeHasMark() {
	if (!mounted) return
	const len = rightViewRef.value?.jsonList?.length || 0
	hasMarkData.value = len > 0
}
function syncPageFlagStatusFromRightView() {
	pageFlagStatus.value = buildPageFlagStatusFromJsonList(rightViewRef.value?.jsonList || [])
}
// rightView 每次画布变化更新完 jsonList 后会 emit updataJsonList，
// 此时读取其 jsonList 长度即与界面所见一致（时序正确）
function onJsonListChange() {
	syncPageFlagStatusFromRightView()
	recomputeHasMark()
}

// ===== 汇总所有页画布数据为 editPageInfoJson =====
// 与 regionalTranslationRightView 提交“区域不翻译”时 rectangleUploadJsonData 的 editPageInfoJson 完全一致：
// { "1": [{ data:[x1,y1,x2,y2,...], edit_rect_type:"area"|"line"|"page", ignore_type:"all" }], ... }
async function buildEditPageInfoJson(): Promise<Record<string, any[]>> {
	const allDrawings = getAllDrawings()
	const result: Record<string, any[]> = {}
	for (const id in allDrawings) {
		const data = await allDrawings[id].exportAllData()
		if (!Array.isArray(data) || data.length === 0) continue
		// id 形如 protect-page-N，split('-')[2] 取页码
		const pageNumber = parseInt(id.split('-')[2], 10)
		if (Number.isNaN(pageNumber)) continue
		const pageKey = String(pageNumber)
		result[pageKey] = data.map((entry: any) => ({
			// rectangle 在后台格式中统一记为 area
			edit_rect_type: entry.edit_rect_type === 'rectangle' ? 'area' : entry.edit_rect_type,
			ignore_type: entry.ignore_type || 'all',
			data: Array.isArray(entry.data) ? entry.data.flat() : []
		}))
	}
	return result
}

// ===== 底部按钮事件 =====
// 【完成选区并关闭】：汇总标记并交给上传组件保存（不触发翻译）
async function onConfirm() {
	const json = await buildEditPageInfoJson()
	emit('confirm', json)
}
// 退出确认弹层显隐（抽屉内自绘，避免 ElMessageBox teleport 到 body 被抽屉遮挡）
const showExitConfirm = ref(false)
// 【退出保护区】：有未保存标记时弹抽屉内确认层；无标记直接退出
function onExit() {
	if (hasMarkData.value) {
		showExitConfirm.value = true
		return
	}
	emit('exit')
}
// 确认层-确定：放弃标记并退出
function onExitConfirmOk() {
	showExitConfirm.value = false
	emit('exit')
}
// 确认层-取消：留在抽屉
function onExitConfirmCancel() {
	showExitConfirm.value = false
}

// ===== 生命周期 =====
onMounted(async () => {
	calcSize()
	window.addEventListener('resize', calcSize)
	// 需求：每次打开保护区抽屉时自动弹一次计费规则浮层。抽屉为 rtl 滑入动画(~300ms)，
	// 等动画结束后再弹，anchorEl(「计费规则」链接)定位才准确；mounted 防卸载后触发。
	setTimeout(() => {
		if (mounted) costRule.open({ lines: buildHfCostLines(), anchorEl: hfCostRuleRef.value })
	}, 400)
	// 回显：读取上传组件保存的标记
	const saved = props.modelValue
	if (saved) {
		editPdfJson.value = saved
		pageFlagStatus.value = buildPageFlagStatusFromEditJson(saved)
		try {
			editPdfJsonStr.value = JSON.stringify(saved)
		} catch (e) {
			editPdfJsonStr.value = ''
		}
	}
	await nextTick()
	await initPdf()
	recomputeHasMark()
	// 直接订阅 drawingManager（rightView 自身亦是此信号源）：
	// 画布每次新增/删除/整页标记变化都重算 hasMarkData，确保「完成选区并关闭」按钮即时出现
	unsubscribeDrawingManager = subscribe(() => {
		recomputeHasMark()
	})
})

onBeforeUnmount(() => {
	mounted = false
	unsubscribeDrawingManager?.()
	unsubscribeDrawingManager = null
	costRule.close()
	window.removeEventListener('resize', calcSize)
	clearDrawings()
	if (resizeRerenderTimer) {
		clearTimeout(resizeRerenderTimer)
		resizeRerenderTimer = null
	}
	if (setDataTimer) {
		clearTimeout(setDataTimer)
		setDataTimer = null
	}
})
</script>

<style scoped lang="less">
.protectArea {
	width: 100%;
	height: 100%;
	display: flex;
	flex-direction: column;
	background-color: #f5f5f5;
	position: relative; // 作为退出确认层 absolute inset:0 的定位上下文

	.protectArea_body {
		flex: 1;
		display: flex;
		overflow: hidden;
	}

	.protectArea_pdf {
		flex: 1;
		min-width: 0;
		position: relative;
		overflow: hidden;

		.loading_view {
			position: absolute;
			inset: 0;
			display: flex;
			align-items: center;
			justify-content: center;
		}

		.protectArea_scroll {
			overflow-y: auto;
			display: flex;
			justify-content: center;
			padding: 10px 0;
		}

		.protectArea_pages {
			display: flex;
			flex-direction: column;
		}
	}

	// ┌─ 右侧栏：列表滚动区 + 底部操作区，纵向二分 ────────────────────────┐
	// │ 操作区不再 absolute 浮空——它就是一个不可压缩的 flex 子项，       │
	// │ 列表拿走剩余高度并自行滚动。选区再多也进不了操作区的地盘，       │
	// │ 遮挡这个分支从此不存在，也不需要给列表底部留任何魔法空白。       │
	// └────────────────────────────────────────────────────────────────────┘
	.protectArea_right {
		flex-shrink: 0;
		box-sizing: border-box;
		position: relative;
		background-color: #fff;
		display: flex;
		flex-direction: column;
		overflow: hidden; // 兜住子项，避免列表把整栏撑出抽屉
	}

	// 列表子组件自带 height:100%，在 flex 列里会与操作区高度叠加而溢出；
	// 这里改回 auto 并交给 flex:1 分配，min-height:0 才允许它真正收缩出滚动条。
	:deep(.regionalTranslationRightView) {
		flex: 1;
		min-height: 0;
		height: auto;
	}

	.protectArea_actions {
		flex: 0 0 auto; // 常驻底部：不参与压缩，不被列表挤走
		box-sizing: border-box;
		width: 100%;
		// 左右 10% 内边距 = 按钮实际占 80% 宽度，与改动前视觉一致；
		// 但边框可以通栏，滚动内容抵达底部时有明确分界
		padding: 12px 10% 20px;
		display: flex;
		flex-direction: column;
		gap: 12px;
		background-color: #fff;
		border-top: 1px solid var(--input-bg-color);
	}

	.protectArea_cost_rule { // 计费规则链接(蓝色,参考 docs/img2)
		text-align: left;
		color: #2590e1;
		font-size: 13px;
		cursor: pointer;
		padding-bottom: 12px;
		border-bottom: 1px solid var(--input-bg-color);
	}

	.protectArea_btn {
		height: 42px;
		line-height: 42px;
		text-align: center;
		border-radius: 6px;
		font-size: 14px;
		cursor: pointer;
		user-select: none;
	}

	.protectArea_btn_primary {
		background-color: rgba(255, 138, 61, 1);
		color: #fff;
	}

	.protectArea_btn_plain {
		background-color: #fff;
		color: var(--text-color2);
		border: 1px solid var(--input-bg-color);
	}

}

// 退出确认层：已 teleport 到 body，提为顶层 scoped 规则（teleport 内容仍带 scoped 属性可命中）；
// fixed 相对浏览器视口居中，不再受抽屉 transform 约束；高 z-index 压住抽屉遮罩
.protectArea_confirm_mask {
		position: fixed;
		inset: 0;
		z-index: 9999;
		background: rgba(0, 0, 0, 0.45);
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.protectArea_confirm_box {
		width: 400px;
		height: 206px;
		max-width: 80%;
		background: #fff;
		border-radius: 10px;
		padding: 24px;
		box-shadow: 0 6px 24px rgba(0, 0, 0, 0.15);
		// 纵向排布：标题 + 内容 + 底部按钮
		display: flex;
		flex-direction: column;
		box-sizing: border-box;
	}

	.protectArea_confirm_title {
		font-size: 16px;
		font-weight: 600;
		color: rgba(38, 38, 38, 1);
		line-height: 1.5;
		margin-bottom: 20px;
		text-align: center;
	}

	.protectArea_confirm_text {
		font-size: 14px;
		color: rgba(38, 38, 38, 1);
		line-height: 1.6;
		white-space: pre-line;
		// 占据剩余空间，把按钮挤到底部
		flex: 1;
		text-align: center;
	}

	.protectArea_confirm_btns {
		display: flex;
		justify-content: flex-end;
		gap: 12px;
	}

	.protectArea_confirm_btn {
		width: 172px;
		height: 40px;
		line-height: 40px;
		text-align: center;
		border-radius: 4px;
		font-size: 14px;
		cursor: pointer;
		user-select: none;
		padding: 0 16px;
	}

	.protectArea_confirm_cancel {
		background: #fff;
		color: rgba(38, 38, 38, 1);
		border: 1px solid rgba(191, 191, 191, 1);
	}

	.protectArea_confirm_ok {
		background-color: rgba(37, 144, 225, 1);
		color: #fff;
	}
</style>
